import { useEffect, useState } from 'react'

import { useCreateUserFeedback } from '../hooks/useCreateUserFeedback'
import { useForm } from 'react-hook-form'

import { Box, Flex, Image, ModalFooter, Text } from '@chakra-ui/react'

import {
  Button,
  FormFileUploadControl,
  FormTextareaControl,
} from '@/components/ui'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'
import { formatBytes } from '@/utils/formats'

import {
  ALLOWED_MIME_TYPES,
  MAX_FILENAME_LENGTH,
  MAX_FILE_SIZE,
} from '../constants/constants'

export interface CreateFeedbackFormInputsIProps {
  message: string
  file?: FileList
}

interface CreateFeedbackFormIProps {
  onClose: () => void
}

export const CreateUserFeedbackForm = ({
  onClose,
}: CreateFeedbackFormIProps) => {
  const { onCreateUserFeedback, isLoading } = useCreateUserFeedback({ onClose })
  const { handleSubmit, register, formState, trigger, watch } =
    useForm<CreateFeedbackFormInputsIProps>({
      mode: 'onChange',
      reValidateMode: 'onChange',
    })

  const { isValid, errors } = formState
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [fileType, setFileType] = useState<string | null>(null)

  const fileList = watch('file')
  const selectedFile = fileList?.[0]

  // Generate preview URL when file is selected
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile)
      setPreviewUrl(url)
      setFileType(selectedFile.type)

      // Cleanup URL when component unmounts or file changes
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setPreviewUrl(null)
      setFileType(null)
    }
  }, [selectedFile])

  const handleSubmitClick = () => {
    mixpanel.track(`${BUTTON_PRESS} Feedback submit`)
    handleSubmit(onCreateUserFeedback)()
  }

  return (
    <>
      <Box mb={3}>
        <FormTextareaControl
          errorMessage={errors.message && errors.message.message}
          id='message'
          isDisabled={isLoading}
          isInvalid={!!errors.message}
          label='Feedback'
          labelFontWeight='500'
          maxCharCount={10000}
          placeholder='Let HiveWatch know how we can improve...'
          rows={6}
          textareaRef={register({
            required: 'Feedback message is required',
            maxLength: {
              value: 10000,
              message: 'Feedback message must not exceed 10000 characters',
            },
          })}
        />
      </Box>

      {/* Media Preview Section */}
      {previewUrl && (
        <Box mb={3} mt={3}>
          <Flex alignItems='center' gap={2}>
            <Box
              border='1px solid'
              borderColor='gray.200'
              borderRadius='md'
              height='50px'
              overflow='hidden'
              position='relative'
              width='50px'
            >
              {fileType?.startsWith('image/') ? (
                <Image
                  alt='Preview'
                  height='100%'
                  objectFit='cover'
                  src={previewUrl}
                  width='100%'
                />
              ) : fileType?.startsWith('video/') ? (
                <Flex
                  alignItems='center'
                  bgColor='#000'
                  height='100%'
                  justifyContent='center'
                  width='100%'
                >
                  <video
                    src={previewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Flex>
              ) : (
                <Flex
                  alignItems='center'
                  bgColor='gray.100'
                  height='100%'
                  justifyContent='center'
                  width='100%'
                >
                  <Text color='gray.600' fontSize='xs' fontWeight='bold'>
                    PDF
                  </Text>
                </Flex>
              )}
            </Box>
            <Text color='gray.600' fontSize='sm' noOfLines={1}>
              {selectedFile?.name}
            </Text>
          </Flex>
        </Box>
      )}

      <Box mb={3}>
        <FormFileUploadControl
          accept='.png,.jpg,.jpeg,.pdf,.mp4,.mov,.mpg,.mpeg'
          errorMessage={errors.file && errors.file.message}
          id='file'
          inputRef={register({
            validate: {
              fileSize: (fileList: FileList) => {
                if (!fileList || fileList.length === 0) return true
                const file = fileList[0]
                return (
                  file.size <= MAX_FILE_SIZE ||
                  `File size must not exceed ${formatBytes(MAX_FILE_SIZE)}`
                )
              },
              fileType: (fileList: FileList) => {
                if (!fileList || fileList.length === 0) return true
                const file = fileList[0]
                return (
                  ALLOWED_MIME_TYPES.includes(file.type) ||
                  'File type not supported. Please upload an image, video, or PDF'
                )
              },
              fileName: (fileList: FileList) => {
                if (!fileList || fileList.length === 0) return true
                const file = fileList[0]
                return (
                  file.name.length <= MAX_FILENAME_LENGTH ||
                  `File name must not exceed ${MAX_FILENAME_LENGTH} characters`
                )
              },
            },
          })}
          isDisabled={isLoading}
          isInvalid={!!errors.file}
          label='Attachment (Optional)'
          onFileRemoved={() => trigger(['file'])}
          placeholder='Attach Media'
        />
      </Box>
      <ModalFooter pb={6} px={0} py={4}>
        <Button mr={3} onClick={onClose} variant='secondary'>
          Cancel
        </Button>
        <Button
          isDisabled={!isValid}
          isLoading={isLoading}
          onClick={handleSubmitClick}
        >
          Submit
        </Button>
      </ModalFooter>
    </>
  )
}
